// Enveloppe d'un projet : navigation latérale + écran courant.
//
// La navigation est déclarée UNE fois ici. L'ancien frontend la reconstruisait
// dans chaque branche du template, et les entrées avaient fini par diverger.
import { ProjectTodoButton } from '../mesure0/ProjectTodo';
import { useEffect, useState, type ReactNode } from 'react';
import { MobileNav } from './MobileNav';
import { CaptureButton } from '../capture/CaptureButton';
import { useIsMobile, useNarrowScreen } from '../components/device';
import { useCanWrite } from '../write/useCanWrite';
import { GuidedTour, tourAlreadySeen } from '../onboarding/GuidedTour';
import { hrefFor, Link, useRouter } from '../router';
import { useMetrics } from '../api/queries';
import { Eyebrow } from '../components/ui';
import { APP_VERSION } from '../version';
import { Icon, type IconName } from '../components/icons';
import type { Hue } from '../taxonomy/colors';
import { NavFilter, ProjectFilterProvider } from '../lifecycle/ProjectFilter';
import './project.css';

export interface ProjectSection {
  slug: string;
  label: string;
  /** Pictogramme et teinte : chaque section se reconnaît d'un coup d'œil. */
  icon: IconName;
  hue: Hue;
  /** Sous-entrées, affichées en retrait : elles appartiennent à la section. */
  children?: ProjectSection[];
}

/** Ordre de travail : on cadre, on observe, on synthétise, on restitue. */
export const SECTIONS: ProjectSection[] = [
  { slug: '', label: 'Tableau de bord', icon: 'dashboard', hue: 'cyan' },
  // Un entretien et un test utilisateur SONT des études : ils se rangent sous
  // « Études » au lieu de lui faire concurrence au premier niveau.
  {
    slug: 'etudes',
    label: 'Études',
    icon: 'flask',
    hue: 'blue',
    children: [
      { slug: 'entretiens', label: 'Entretiens', icon: 'mic', hue: 'orange' },
      { slug: 'tests', label: 'Tests utilisateurs', icon: 'click', hue: 'violet' },
    ],
  },
  // L'atelier est la synthèse FAITE À LA MAIN : les verbatims en post-it, que
  // l'on regroupe pour en tirer des insights et les porter sur les personas.
  { slug: 'atelier', label: 'Atelier', icon: 'board', hue: 'amber' },
  // Deux onglets : l'annuaire des vraies personnes (vivier des études) et les
  // personas (profils types). Le chemin reste « personas » : les liens tiennent.
  { slug: 'personas', label: 'Utilisateurs et personas', icon: 'users', hue: 'pink' },
  { slug: 'insights', label: 'Insights', icon: 'bulb', hue: 'amber' },
  // Les tags admis pour ranger les verbatims : un outil de travail, plus un réglage.
  { slug: 'taxonomie', label: 'Taxonomie', icon: 'tag', hue: 'violet' },
  { slug: 'triangulation', label: 'Triangulation', icon: 'triangle', hue: 'green' },
  { slug: 'studio', label: 'Studio de restitution', icon: 'presentation', hue: 'red' },
  { slug: 'copilote', label: 'Copilote', icon: 'sparkles', hue: 'cyan' },
  // Les réglages du projet (« options ») ne sont pas une section de travail :
  // on y accède par la roue dentée, à côté du nom du projet actif.
];

/** Roue dentée, trait de 1,5 px comme les autres pictogrammes. */
function GearIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

export function ProjectLayout({
  projectId,
  projectName,
  section,
  children,
}: {
  projectId: string;
  projectName: string;
  section: string;
  children: ReactNode;
}) {
  const base = `/projet/${encodeURIComponent(projectId)}`;
  const { navigate, path } = useRouter();
  // La visite guidée s'ouvre seule la première fois, puis par le bouton « ? ».
  const [touring, setTouring] = useState(() => !tourAlreadySeen());
  // Sur téléphone, le menu est un TIROIR : fermé par défaut, il s'ouvre depuis
  // la barre du bas et se referme dès qu'on a choisi un écran.
  const [menuOpen, setMenuOpen] = useState(false);
  const mobile = useIsMobile();
  const canWrite = useCanWrite(projectId);
  useEffect(() => setMenuOpen(false), [path]);
  // Sur téléphone, la visite guidée montre les entrées du menu : le tiroir
  // s'ouvre le temps de la visite.
  const narrow = useNarrowScreen();
  useEffect(() => {
    if (narrow) setMenuOpen(touring);
  }, [touring, narrow]);
  useEffect(() => {
    if (!menuOpen) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [menuOpen]);
  const optionsTo = `${base}/options`;
  const optionsOn = section === 'options';
  // Le nombre au bout de chaque ligne vient des compteurs du tableau de bord :
  // même requête, déjà en cache, donc le menu et les indicateurs disent pareil.
  const raw = useMetrics(projectId).data?.raw;
  const counts: Record<string, number | undefined> = {
    etudes: raw?.experiments.studies,
    entretiens: raw?.interviews.total,
    tests: raw?.experiments.tests,
    personas: raw?.coverage.personas,
    insights: raw?.insights.total,
    triangulation: raw?.triangulation.measures,
  };

  // Une entrée du menu, et ses sous-entrées s'il y en a (même rendu, en retrait).
  const navItem = (s: ProjectSection, sub = false) => {
    const to = s.slug ? `${base}/${s.slug}` : base;
    const on = s.slug === section;
    // Sur une sous-entrée, la section parente reste repérable sans prendre la
    // sélection : une seule ligne est « la page courante ».
    const within = !on && (s.children?.some((c) => c.slug === section) ?? false);
    return (
      <li key={s.slug || 'dashboard'} className={s.children ? 'nav-group' : undefined}>
        <a
          href={hrefFor(to)}
          data-tour={`nav-${s.slug || 'dashboard'}`}
          className={`nav-item${sub ? ' nav-item-sub' : ''}${on ? ' nav-item-on' : ''}${within ? ' nav-item-within' : ''}`}
          aria-current={on ? 'page' : undefined}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            e.preventDefault();
            navigate(to);
          }}
        >
          <span className={`nav-icon hue-${s.hue}`}>
            <Icon name={s.icon} size={sub ? 13 : 16} />
          </span>
          <span>{s.label}</span>
          {counts[s.slug] !== undefined ? (
            <span className="nav-count">{counts[s.slug]}</span>
          ) : null}
        </a>
        {s.children ? <ul className="nav-sub">{s.children.map((c) => navItem(c, true))}</ul> : null}
      </li>
    );
  };

  return (
    // Le filtre enveloppe le menu ET l'écran : il s'applique à tout le projet.
    <ProjectFilterProvider projectId={projectId}>
    <div className={`project-layout${menuOpen ? ' is-menu-open' : ''}`}>
      {/* Téléphone : une barre fine en haut, pour savoir où l'on est. */}
      <div className="project-mobilebar">
        <Link to="/" className="project-mobilebar-back" aria-label="Tous les projets">
          ←
        </Link>
        <span className="project-mobilebar-name">{projectName}</span>
        <ProjectTodoButton projectId={projectId} projectName={projectName} />
      </div>
      {menuOpen ? (
        <button type="button" className="project-nav-scrim" aria-label="Fermer le menu" onClick={() => setMenuOpen(false)} />
      ) : null}
      <nav className="project-nav" aria-label="Sections du projet">
        <div className="project-nav-head">
          <Link to="/" className="zy-retour">
            ← Tous les projets
          </Link>
          <div className="project-nav-title">
            <Eyebrow>Projet actif</Eyebrow>
            <span className="project-nav-tools">
            {/* Les recommandations : ce qu'il reste à définir (ex-bloc de l'accueil). */}
            <ProjectTodoButton projectId={projectId} projectName={projectName} />
            <button
              type="button"
              className="nav-gear nav-tour"
              data-tour="tour"
              aria-label="Visite guidée de l’interface"
              title="Visite guidée de l’interface"
              onClick={() => setTouring(true)}
            >
              ?
            </button>
            <a
              href={hrefFor(optionsTo)}
              data-tour="options"
              className={`nav-gear${optionsOn ? ' nav-gear-on' : ''}`}
              aria-label="Options du projet"
              title="Options du projet : équipe, connecteurs, cycle de vie"
              aria-current={optionsOn ? 'page' : undefined}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                e.preventDefault();
                navigate(optionsTo);
              }}
            >
              <GearIcon />
            </a>
            </span>
          </div>
          <p className="project-nav-name">{projectName}</p>
          <NavFilter projectId={projectId} />
        </div>
        <ul>{SECTIONS.map((s) => navItem(s))}</ul>
        {/* La version livrée : ce qu'on cite quand on signale un problème. */}
        <p className="project-nav-version"><Link to="/versions">Zygosight v{APP_VERSION}</Link></p>
      </nav>
      <div className="project-content">{children}</div>
      <MobileNav
        base={base}
        section={section}
        menuOpen={menuOpen}
        onMenu={() => setMenuOpen((v) => !v)}
        // Capturer : un geste de terrain, en mode mobile et pour qui peut écrire.
        capture={mobile && canWrite ? <CaptureButton projectId={projectId} /> : undefined}
      />
      <GuidedTour open={touring} onClose={() => setTouring(false)} />
    </div>
    </ProjectFilterProvider>
  );
}
